import { useLazyQuery } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';

import {
  GetMessagePatientProfileDocument,
  GetMessagePatientProfileQuery,
} from '~/graphql/generated/graphql';
import { messageModalState } from '~/state/partials/message_modal/atoms';
import { Profile } from '~/state/partials/message_modal/types';

const toPatientProfile = (data?: GetMessagePatientProfileQuery): Profile | null => {
  if (!data?.node) return null;
  if (data.node.__typename === 'DirectVisitor') {
    const visitor = data.node;

    return {
      type: visitor.__typename,
      appointmentId: null,
      patientId: null,
      guestPatientId: visitor.id,
      familyName: visitor.familyName,
      givenName: visitor.givenName,
      phoneticFamilyName: visitor.phoneticFamilyName,
      phoneticGivenName: visitor.phoneticGivenName,
      visitDay: visitor.createdAt,
    };
  }

  if (data.node.__typename === 'WebVisitor') {
    const visitor = data.node;
    const appointment = visitor.appointment;

    return {
      type: visitor.__typename,
      appointmentId: appointment.id,
      patientId: null,
      guestPatientId: visitor.id,
      familyName: visitor.familyName,
      givenName: visitor.givenName,
      phoneticFamilyName: visitor.phoneticFamilyName,
      phoneticGivenName: visitor.phoneticGivenName,
      visitDay: appointment.start || undefined,
    };
  }

  if (data.node.__typename === 'Patient') {
    const patient = data.node;
    const appointment = patient.appointments.nodes[0];

    return {
      type: patient.__typename,
      appointmentId: appointment.id,
      patientId: patient.id,
      guestPatientId: null,
      familyName: patient.familyName,
      givenName: patient.givenName,
      phoneticFamilyName: patient.phoneticFamilyName,
      phoneticGivenName: patient.phoneticGivenName,
      visitDay: appointment.start || undefined,
    };
  }

  return null;
};

export const useFetchPatientProfile = () => {
  const [{ patientId }, setState] = useAtom(messageModalState);
  const [getPatient, { called, loading, data }] = useLazyQuery(GetMessagePatientProfileDocument, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const profile = useMemo(() => toPatientProfile(data), [data]);

  useEffect(() => {
    if (patientId) {
      getPatient({
        variables: {
          id: patientId,
        },
      });
    }
  }, [getPatient, patientId]);

  useEffect(() => {
    setState((_state) => ({ ..._state, profile }));
  }, [profile, setState]);

  return {
    loading: !called || loading,
    profile,
  };
};
